import {
  IOnboardingInvoicing,
  IOnboardingWizardState,
  IOnboardingXeroKey,
} from "../../Interfaces/IOnboardingWizardState";

/** Typed codes are uppercase alphanumerics and stop at ten characters (S1.3). */
export const normaliseCompanyCode = (input: string | null | undefined): string =>
  `${input ?? ""}`
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);

export const isCompanyCodeFormatValid = (code: string | null | undefined): boolean =>
  /^[A-Z0-9]{5,10}$/.test(`${code ?? ""}`);

export const COMPANY_CODE_FORMAT_MESSAGE = "Use 5–10 uppercase letters or numbers";

/** Sub/Division Code is uppercased as typed (S1.1). */
export const upperCaseAsTyped = (input: string | null | undefined): string =>
  `${input ?? ""}`.toUpperCase();

export const isValidEmail = (value: string | null | undefined): boolean =>
  /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/.test(`${value ?? ""}`.trim());

export const FIX_HIGHLIGHTED_FIELDS = "Please fix the highlighted fields";

/** The five fields step 1 marks required, and nothing else (S1.5). */
export const STEP1_REQUIRED_FIELDS = [
  "companyName",
  "companyCode",
  "franchiseID",
  "countryCode",
  "primaryCompanyRelationshipManager",
] as const;

/**
 * Step 5's advance validation, in the order S5.10 states it — the first failure is the one shown.
 * The completion engine on the server applies the same conditions, so the meter can never read
 * complete while this returns a message.
 */
export const step5ValidationMessage = (
  invoicing: IOnboardingInvoicing | null,
  isOtherArrangement: boolean,
): string | null => {
  if (!invoicing?.arrangement) return "Select how the client is invoiced.";
  if (isOtherArrangement && !invoicing.otherArrangementDescription?.trim())
    return "Describe the invoicing arrangement.";

  const keys = invoicing.xeroKeys || [];
  if (!keys.length) return "Add at least one Xero key.";
  if (keys.some((key) => !key.name?.trim() || !key.keyValue?.trim() || !key.invoiceTiming))
    return "Complete every Xero key and its allocation.";
  if (keys.some((key, index) => xeroKeyNameProblem(keys, index))) return FIX_HIGHLIGHTED_FIELDS;
  // With one key only, the allocation is not checked at all (S5.10).
  if (
    keys.length > 1 &&
    keys.some(
      (key) =>
        (!key.allServices && !(key.serviceCategories || []).length) ||
        (!key.allOffices && !(key.siteIDs || []).length),
    )
  )
    return "Complete every Xero key and its allocation.";

  if (!isValidEmail(invoicing.invoiceEmail)) return FIX_HIGHLIGHTED_FIELDS;

  return null;
};

/**
 * Imperans caps a Xero key name at 50 characters and wants each name distinct (Part R, R.5 S13).
 * Round 1 testing typed a 58-character name past the step and the self-check, and Imperans
 * answered 422 (bug 6495) — so the field says so as it is typed, and the step will not advance.
 */
export const XERO_KEY_NAME_MAX_LENGTH = 50;

/** The name problem on one key — too long or a repeat of another key's name — or null when fine. */
export const xeroKeyNameProblem = (keys: IOnboardingXeroKey[], index: number): string | null => {
  const name = `${keys[index]?.name ?? ""}`.trim();
  if (!name) return null;
  if (name.length > XERO_KEY_NAME_MAX_LENGTH)
    return `Shorten the name to ${XERO_KEY_NAME_MAX_LENGTH} characters or fewer.`;
  const repeated = keys.some(
    (other, otherIndex) =>
      otherIndex !== index && `${other.name ?? ""}`.trim().toLowerCase() === name.toLowerCase(),
  );
  return repeated ? "Give each Xero key a different name." : null;
};

/** Which Xero keys are incomplete, so the fields themselves can be highlighted (S5.10). */
export const incompleteXeroKeyIndexes = (
  keys: IOnboardingXeroKey[],
  requireAllocation: boolean,
): number[] =>
  keys
    .map((key, index) => {
      const missingCore = !key.name?.trim() || !key.keyValue?.trim() || !key.invoiceTiming;
      const missingAllocation =
        requireAllocation &&
        ((!key.allServices && !(key.serviceCategories || []).length) ||
          (!key.allOffices && !(key.siteIDs || []).length));
      return missingCore || missingAllocation || xeroKeyNameProblem(keys, index) ? index : -1;
    })
    .filter((index) => index >= 0);

/** A client always has at least one key, named Primary, and the list can never empty (S5.2). */
export const seedXeroKeys = (keys: IOnboardingXeroKey[] | null | undefined): IOnboardingXeroKey[] =>
  keys && keys.length
    ? keys
    : [
        {
          name: "Primary",
          keyValue: null,
          invoiceTiming: null,
          allServices: true,
          allOffices: true,
          serviceCategories: [],
          siteIDs: [],
        },
      ];

/** Step 6 blocks with the outstanding count, and No counts as an answer (S6.1). */
export const step6ValidationMessage = (
  state: IOnboardingWizardState | null,
): string | null => {
  const questions = state?.siteQuestions || [];
  if (!questions.length) return null;
  const answers = state?.siteRequirements?.answers || {};
  const outstanding = questions.filter(
    (question) =>
      answers[question.key] === null || answers[question.key] === undefined,
  ).length;
  if (!outstanding) return null;
  return `Answer all ${questions.length} site questions (${outstanding} left).`;
};

export const OCCF_NOTES_MAX_LENGTH = 1000;

/** OCCF Notes hard-cap at 1000 characters; an over-length paste truncates at the cap (S6.8). */
export const capOccfNotes = (value: string | null | undefined): string =>
  `${value ?? ""}`.slice(0, OCCF_NOTES_MAX_LENGTH);

/**
 * A client on both service lines may need a TDDA and a HealthTick purchase-order number, so the
 * second number is offered only when HealthTick is in the line (S5.5).
 */
export const showsHealthTickPurchaseOrder = (
  state: IOnboardingWizardState | null,
): boolean =>
  Boolean(state?.isHealthTickEligible) &&
  /healthtick|both/i.test(state?.services?.serviceLine || "");

/**
 * Step 1's Client Type control used to offer SSO only once the client's country was Australia (story
 * 5590's final bullet, bug 5845). The ruling of 5 Oct 2026 made Auckland an SSO covering Auckland
 * East and Auckland West, so every country offers what the server sends (bug 6491). Kept as the one
 * place a country rule would go back in, and so the change of country still re-checks the pick.
 */
export const clientTypesForCountry = (
  clientTypes: string[],
  _countryCode: string | null | undefined,
): string[] => clientTypes;
